import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { ImagePlus, X } from "lucide-react";

const LOGO_TYPES = ["image/png", "image/jpeg", "image/svg+xml"];
const inputCls = "w-full px-3 py-2.5 rounded-lg border focus:outline-none focus:border-blue-500";

function LogoPicker({ file, onChange }) {
  const [preview, setPreview] = useState("");
  useEffect(() => {
    if (!file) { setPreview(""); return; }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!LOGO_TYPES.includes(f.type)) { toast.error("Logo must be a PNG, JPG or SVG image"); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be 2 MB or smaller"); return; }
    onChange(f);
  };
  return (
    <div className="flex items-center gap-3" data-testid="register-logo-picker">
      {preview ? (
        <div className="relative">
          <img src={preview} alt="Logo preview" className="h-12 w-auto max-w-[120px] object-contain rounded bg-white/5 p-1 border border-slate-700" data-testid="register-logo-preview" />
          <button type="button" onClick={() => onChange(null)} data-testid="register-logo-remove" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-slate-800 border border-slate-600 grid place-items-center text-slate-300 hover:text-white"><X size={11} /></button>
        </div>
      ) : (
        <div className="h-12 w-12 rounded border border-dashed border-slate-600 grid place-items-center text-slate-500"><ImagePlus size={18} /></div>
      )}
      <label className="text-sm text-blue-400 hover:text-blue-300 cursor-pointer">
        {file ? "Change logo" : "Upload logo or business image (optional)"}
        <input type="file" accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml" className="hidden" onChange={pick} data-testid="register-logo-input" />
      </label>
    </div>
  );
}

export default function Register() {
  const { register, refresh } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountType, setAccountType] = useState("individual");
  const [orgName, setOrgName] = useState("");
  const [division, setDivision] = useState("");
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const ent = accountType === "enterprise";
    if (ent && !orgName.trim()) { setErr("Company name is required for enterprise accounts"); setBusy(false); return; }
    try {
      await register({ name, email, password, account_type: accountType, organization_name: ent ? orgName.trim() : null, division_name: ent ? division.trim() : null });
      if (ent && logo) {
        const fd = new FormData();
        fd.append("file", logo);
        try { await api.post("/organizations/me/logo", fd); await refresh(); }
        catch (e3) { toast.error(`Account created, but the logo upload failed: ${errMsg(e3)}. You can add it later in Settings.`); }
      }
      toast.success("Account created — 14-day trial started");
      nav("/app", { replace: true });
    } catch (e2) { setErr(errMsg(e2)); }
    finally { setBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen pc-grid-bg flex items-center justify-center p-6">
      <div className="pc-card p-8 w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <span className="text-white font-bold" style={{fontFamily:'Outfit'}}>P</span>
          </div>
          <div className="font-bold text-white text-lg" style={{fontFamily:'Outfit'}}>Compass PM</div>
        </Link>
        <h1 className="text-2xl font-semibold text-white mb-1" style={{fontFamily:'Outfit'}}>Start your trial</h1>
        <p className="text-sm text-slate-400 mb-6">14 days free — no credit card required.</p>

        <button onClick={googleSignIn} data-testid="google-signup-btn"
          className="w-full mb-4 flex items-center justify-center gap-2 bg-white text-slate-900 px-4 py-2.5 rounded-lg font-medium hover:bg-slate-100">
          Continue with Google
        </button>
        <div className="flex items-center gap-2 my-4"><div className="h-px bg-slate-800 flex-1"/><span className="text-xs text-slate-500">OR</span><div className="h-px bg-slate-800 flex-1"/></div>

        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2 mb-2">
            {["individual","enterprise"].map((t)=> (
              <button type="button" key={t} data-testid={`account-type-${t}`} onClick={()=>setAccountType(t)}
                className={`px-3 py-2 rounded-lg text-sm border ${accountType===t ? "border-blue-500 bg-blue-500/10 text-blue-300" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
                {t==="individual" ? "Individual" : "Enterprise"}
              </button>
            ))}
          </div>
          {accountType === "enterprise" && (
            <div className="space-y-3 p-3 rounded-lg border border-slate-800 bg-slate-900/40" data-testid="register-enterprise-fields">
              <input data-testid="register-org" placeholder="Company name" value={orgName} onChange={(e)=>setOrgName(e.target.value)} className={inputCls} />
              <input data-testid="register-division" placeholder="Division name (optional)" value={division} onChange={(e)=>setDivision(e.target.value)} className={inputCls} />
              <LogoPicker file={logo} onChange={setLogo} />
              <p className="text-[11px] text-slate-500">PNG, JPG or SVG, up to 2 MB. You can change these later in Settings.</p>
            </div>
          )}
          <input data-testid="register-name" required placeholder="Your name" value={name} onChange={(e)=>setName(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border focus:outline-none focus:border-blue-500" />
          <input data-testid="register-email" required type="email" placeholder="Work email" value={email} onChange={(e)=>setEmail(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border focus:outline-none focus:border-blue-500" />
          <input data-testid="register-password" required type="password" placeholder="Password (8+ characters)" minLength={8} value={password} onChange={(e)=>setPassword(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border focus:outline-none focus:border-blue-500" />
          {err && <div data-testid="register-error" className="text-sm text-rose-400">{err}</div>}
          <button data-testid="register-submit" disabled={busy}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white py-2.5 rounded-lg font-semibold shadow-lg shadow-blue-900/30 hover:from-blue-400 hover:to-blue-600 disabled:opacity-60">
            {busy ? "Creating…" : "Start free trial"}
          </button>
        </form>

        <div className="mt-6 text-sm text-slate-400 text-center">
          Have an account? <Link to="/login" className="text-blue-400 hover:text-blue-300" data-testid="go-login-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
